import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Floating CTA shown on /blog/:slug pages only.
 * - Mobile: thin coral strip pinned above the MobileBottomBar (which is h-14).
 * - Desktop: 320px dark card pinned to bottom-right with a coral button.
 * - Hidden while user is in the first 30% of the page (so it doesn't compete
 *   with the hero CTA).
 * - User can dismiss via X — flag stored per session (sessionStorage),
 *   scoped to the post slug so dismissing one post doesn't hide on others.
 */

const CATEGORY_SERVICE_MAP = {
    fencing: { slug: "fencing", message: "Get a free quote on your fence." },
    decking: { slug: "decking", message: "Get a free quote on your deck." },
    interlocking: {
        slug: "interlocking",
        message: "Get a free quote on your driveway or walkway.",
    },
    concrete: {
        slug: "concrete",
        message: "Get a free quote on your concrete or asphalt work.",
    },
    landscaping: {
        slug: "landscaping",
        message: "Get a free quote on your landscaping project.",
    },
    renovations: {
        slug: "renovations",
        message: "Get a free quote on your renovation.",
    },
    general: { slug: null, message: "Get a free quote on your project." },
};

const SCROLL_THRESHOLD = 0.3;

const dismissKey = (slug) => `blog-sticky-cta-dismissed:${slug || "_"}`;

const BlogStickyCta = ({ category = "General", slug = "", contactPath = "/contact" }) => {
    const key = (category || "general").toLowerCase();
    const mapping = CATEGORY_SERVICE_MAP[key] || CATEGORY_SERVICE_MAP.general;

    const initialDismissed = (() => {
        if (typeof window === "undefined") return false;
        try {
            return sessionStorage.getItem(dismissKey(slug)) === "1";
        } catch {
            return false;
        }
    })();

    const [dismissed, setDismissed] = useState(initialDismissed);
    const [scrolledEnough, setScrolledEnough] = useState(false);

    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement;
            const total = doc.scrollHeight - doc.clientHeight;
            const ratio = total > 0 ? doc.scrollTop / total : 0;
            setScrolledEnough(ratio > SCROLL_THRESHOLD);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Re-check dismissed flag when slug changes (navigating between posts).
    useEffect(() => {
        try {
            setDismissed(sessionStorage.getItem(dismissKey(slug)) === "1");
        } catch {
            setDismissed(false);
        }
    }, [slug]);

    const handleDismiss = () => {
        try {
            sessionStorage.setItem(dismissKey(slug), "1");
        } catch {
            /* ignore quota / private mode */
        }
        setDismissed(true);
    };

    const visible = scrolledEnough && !dismissed;

    const ctaHref = mapping.slug
        ? `${contactPath}?service=${mapping.slug}&utm_source=blog&utm_content=${slug || ""}`
        : `${contactPath}?utm_source=blog&utm_content=${slug || ""}`;

    return (
        <>
            {/* Mobile: thin strip pinned above the MobileBottomBar (h-14 = 56px).
                Use bottom-16 (= 64px) so there's a clean 8px gap above the bar
                regardless of subpixel rounding. */}
            <div
                className={cn(
                    "md:hidden fixed inset-x-0 z-30 transition-all duration-300 ease-out",
                    "bottom-16",
                    visible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-full opacity-0 pointer-events-none",
                )}
                aria-hidden={!visible}
                data-testid={visible ? "blog-sticky-cta-mobile" : undefined}
            >
                <div className="bg-brand-coral text-white shadow-[0_-4px_14px_rgba(0,0,0,0.18)] flex items-center justify-between gap-2 px-4 py-2.5">
                    <Link
                        to={ctaHref}
                        className="flex-1 flex items-center justify-between gap-2 text-sm font-semibold"
                        data-testid="blog-sticky-cta-link-mobile"
                    >
                        <span className="line-clamp-1">{mapping.message}</span>
                        <span className="inline-flex items-center gap-1 uppercase tracking-wider text-xs font-bold whitespace-nowrap">
                            Free Quote
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </span>
                    </Link>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="shrink-0 -mr-1 p-1.5 text-white/85 hover:text-white transition-colors"
                        aria-label="Dismiss"
                        data-testid="blog-sticky-cta-dismiss-mobile"
                    >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                </div>
            </div>

            {/* Desktop: floating dark card pinned bottom-right, shifted left to
                make room for the ChatWidget launcher at bottom-6 right-6. */}
            <div
                className={cn(
                    "hidden md:block fixed bottom-6 right-[110px] z-30 w-80 transition-all duration-300 ease-out",
                    visible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-4 opacity-0 pointer-events-none",
                )}
                aria-hidden={!visible}
                data-testid={visible ? "blog-sticky-cta-desktop" : undefined}
            >
                <div className="relative bg-brand-dark text-white shadow-2xl border border-white/10 p-5">
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="absolute top-2 right-2 p-1.5 text-white/60 hover:text-white transition-colors"
                        aria-label="Dismiss"
                        data-testid="blog-sticky-cta-dismiss-desktop"
                    >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-2">
                        Need this work done?
                    </p>
                    <p className="font-display text-2xl tracking-wide leading-tight text-white mb-4">
                        {mapping.message}
                    </p>
                    <Link
                        to={ctaHref}
                        className="inline-flex w-full items-center justify-center gap-2 bg-brand-coral hover:bg-[#d04438] text-white font-semibold uppercase tracking-wider text-xs px-5 h-11 transition-colors duration-200"
                        data-testid="blog-sticky-cta-link-desktop"
                    >
                        Get a Free Quote
                        <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                    </Link>
                </div>
            </div>
        </>
    );
};

export default BlogStickyCta;
